import { migrateDocument, type HomecadDocument } from "@homecad/schema";

/**
 * Sortir une piece de HomeCAD, et l'y faire rentrer.
 *
 * L'export STL ne sort que le maillage : la pièce y perd son arbre de features,
 * ses parametres et ses formules. Un STL ne se reprend pas, il s'imprime. Le
 * JSON du document, lui, est **la piece elle-meme** — c'est ce qu'il faut pour
 * archiver hors ligne, envoyer a quelqu'un, ou reprendre ailleurs.
 */

/**
 * Un nom en fragment de nom de fichier : lisible, sans espace, et qui ne
 * surprend aucun systeme. Exporte parce que trois exports s'en servent — le
 * JSON, le STL entier et les pieces d'un debit — et que trois regles differentes
 * pour le meme besoin finissent toujours par se contredire.
 */
export function nomSansExtension(nom: string): string {
  const propre = nom
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  return propre === "" ? "piece" : propre;
}

/** Fait descendre un fichier chez l'utilisateur. */
export function telecharger(blob: Blob, nom: string): void {
  const url = URL.createObjectURL(blob);
  const lien = window.document.createElement("a");
  lien.href = url;
  lien.download = nom;
  lien.click();
  URL.revokeObjectURL(url);
}

export function exporterDocument(document: HomecadDocument): void {
  // Indente : ce fichier finira dans un depot git ou dans un courriel, et un
  // JSON sur une seule ligne n'y est ni lisible ni comparable.
  const blob = new Blob([JSON.stringify(document, null, 2)], { type: "application/json" });
  telecharger(blob, `${nomSansExtension(document.name)}.homecad.json`);
}

export class ImportInvalide extends Error {}

/**
 * Lit un fichier choisi par l'utilisateur. Le document passe par
 * `migrateDocument`, donc par la montee de version **et** par le controle des
 * references : un fichier bricole a la main, ou exporte d'une version
 * anterieure, est traite comme tout autre document entrant.
 *
 * On ne fait pas confiance a l'extension : c'est le contenu qui decide.
 */
export async function lireDocumentImporte(fichier: File): Promise<HomecadDocument> {
  if (fichier.size > 8 * 1024 * 1024) {
    throw new ImportInvalide("Ce fichier dépasse 8 Mo : ce n'est probablement pas une pièce.");
  }
  const texte = await fichier.text();
  let brut: unknown;
  try {
    brut = JSON.parse(texte);
  } catch {
    throw new ImportInvalide("Ce fichier n'est pas du JSON.");
  }
  try {
    return migrateDocument(brut);
  } catch (cause) {
    throw new ImportInvalide(
      cause instanceof Error ? cause.message : "Ce fichier n'est pas une pièce HomeCAD.",
    );
  }
}

/** Le nom propose a l'import : celui du document, sinon celui du fichier. */
export function nomPropose(document: HomecadDocument, fichier: File): string {
  const duDocument = document.name.trim();
  if (duDocument !== "" && duDocument !== "demo") return duDocument;
  return fichier.name.replace(/\.homecad\.json$|\.json$/i, "") || "Pièce importée";
}
